import React, { useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";

interface AlertProps {
    children?: ReactNode;
    variant?: "warning" | "info" | "success" | "danger" | "error";
    centered?: boolean;
    className?: string;
    copyable?: boolean;
    copyText?: string;
    icon?: string | null;
    noicon?: boolean;
    value?: ReactNode;
    text?: string;
    align?: "left" | "center" | "right";
}

const variants = {
    warning:
        "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200",
    info: "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800/60 dark:bg-blue-950/30 dark:text-blue-200",
    success:
        "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/30 dark:text-emerald-200",
    danger: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800/60 dark:bg-rose-950/30 dark:text-rose-200",
    error: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800/60 dark:bg-rose-950/30 dark:text-rose-200",
};

const alignClasses = {
    left: "",
    center: "justify-center text-center",
    right: "justify-end text-right",
};

export const Alert = ({
    children,
    variant = "info",
    centered = false,
    className = "",
    copyable,
    copyText,
    icon = null,
    noicon = false,
    value,
    text = "",
    align = "left",
}: AlertProps) => {
    const { t } = useTranslation("global");
    const [copied, setCopied] = useState<boolean>(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const isCopyable = copyable === true;

    const finalAlign = centered ? "center" : align;

    const handleCopy = async (): Promise<void> => {
        if (!isCopyable) {
            return;
        }

        const textToCopy = value ?? children;
        const text =
            copyText ??
            (typeof textToCopy === "string" || typeof textToCopy === "number"
                ? String(textToCopy)
                : (contentRef.current?.textContent?.trim() ?? ""));

        if (!text) {
            return;
        }

        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch {
            // clipboard unavailable
        }
    };

    return (
        <div
            role={
                variant === "error" || variant === "danger" ? "alert" : "status"
            }
            aria-atomic="true"
            onClick={
                isCopyable
                    ? () => {
                          void handleCopy();
                      }
                    : undefined
            }
            onKeyDown={
                isCopyable
                    ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              void handleCopy();
                          }
                      }
                    : undefined
            }
            tabIndex={isCopyable ? 0 : undefined}
            title={
                isCopyable
                    ? copied
                        ? t("error.copied")
                        : t("error.copy_message")
                    : undefined
            }
            className={`group/alert flex items-center gap-2 rounded-2xl border px-3 py-2.5 text-xs leading-relaxed ${variants[variant]} ${alignClasses[finalAlign]} ${isCopyable ? "cursor-pointer transition-colors hover:brightness-[0.98] dark:hover:brightness-110 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-rose-400/60" : ""} ${className}`}
        >
            {!noicon ? (
                icon ? (
                    icon
                ) : (
                    <Icon
                        value={
                            variant === "warning" ||
                            variant === "danger" ||
                            variant === "error"
                                ? "alertTriangle"
                                : "info"
                        }
                        size={16}
                        className="shrink-0"
                    />
                )
            ) : null}
            <div
                ref={contentRef}
                className={`min-w-0 ${finalAlign === "center" ? "" : "flex-1"}`}
            >
                {text && text?.length > 0 ? text : value ? value : children}
            </div>
            {isCopyable && (
                <span className="shrink-0 opacity-70 transition-opacity group-hover/alert:opacity-100">
                    <Icon
                        value={copied ? "check" : "copy"}
                        size={14}
                        className={
                            copied
                                ? "text-emerald-600 dark:text-emerald-400"
                                : ""
                        }
                    />
                </span>
            )}
        </div>
    );
};
